/**
 * ReimbursementsTable – desktop table for the reimbursements list.
 * Config-driven columns (mirrors the application module's column system):
 * columns are declared once in `REIMBURSEMENTS_LIST_COLUMN_OPTIONS` and
 * rendered through `renderColumnHeader`/`renderColumnCell` keyed by column id,
 * with visibility/order driven by the "Columns" box props.
 *
 * Angular `default-list` does not expand a per-row inline edit on row click
 * (the inline `residentEditForm` block is never shown because the toggle is commented out);
 * list edits are done on the detail route instead.
 */
import { Fragment, useMemo, type ReactNode } from 'react'
import { ChevronsUpDown } from 'lucide-react'
import { Link } from 'react-router-dom'
import { ROUTE_BUILDERS } from '@/config/routes'
import { useUserRole } from '@/hooks/useUserRole'
import { cn } from '@/lib/utils/helpers'
import { AccessConfirmationIconButton } from '@/components/ui/AccessConfirmationIconButton'
import {
  StatementListColumnHeader,
  StatementListDownloadIconButton,
} from '@/components/ui/StatementListIcon'
import styles from '../styles/listingTables.module.css'
import { LIBERTY_ROLES } from '../constants'
// import { DocumentStatusInfoIcon } from './DocumentStatusInfoIcon'
import {
  formatCurrency,
  formatDate,
  formatYear,
} from '../utils/formatters'
import {
  displayDocumentStatus,
  padApplicantId,
} from '../utils/statusMaps'
import {
  REIMBURSEMENTS_LIST_COLUMN_OPTIONS,
  isReimbursementListLockedColumn,
  type ReimbursementListColumnId,
} from '../utils/reimbursementListColumns'
import {
  reimbursementTypeLabel,
  reimbursementTypeTextClass,
} from '@/features/resident/utils/residentListStatusStyles'
import type { ReimbursementListItem } from '../types'
import type { ReimbursementsTableProps, SortHeaderProps } from '../types'
import {
  getReimbursementListProgressPillClassName,
  getReimbursementWorkflowStatusPillClassName,
  resolveReimbursementListProgressLabel,
} from '../utils/reimbursementHeaderSummaryFields'

/** Column widths in rem, keyed by column id (drives the `<colgroup>`). */
const COLUMN_WIDTHS: Record<ReimbursementListColumnId, number> = {
  applicant_id: 6.5,
  resident_name: 9,
  property_name: 10,
  company_name: 13.5,
  agent_name: 11.5,
  certificate_amount: 9.5,
  default_paid: 6.5,
  reimbursement_type: 10.5,
  reimbursement_rental_status: 11,
  reimbursement_rental_progress: 12,
  review_decision: 8.5,
  document_status: 10,
  contract_start_date: 9,
  vintage: 7,
  rental_status_date: 12.5,
  adjust_max_movein_date: 8.5,
  default_status_date: 12.5,
  access_confirmation: 8.5,
  statement: 7,
}

/** Header + cell text alignment — uniform centered for every column. */
const COLUMN_ALIGN: Record<ReimbursementListColumnId, 'start' | 'center' | 'end'> = {
  applicant_id: 'center',
  resident_name: 'center',
  property_name: 'center',
  company_name: 'center',
  agent_name: 'center',
  certificate_amount: 'center',
  default_paid: 'center',
  reimbursement_type: 'center',
  reimbursement_rental_status: 'center',
  reimbursement_rental_progress: 'center',
  review_decision: 'center',
  document_status: 'center',
  contract_start_date: 'center',
  vintage: 'center',
  rental_status_date: 'center',
  adjust_max_movein_date: 'center',
  default_status_date: 'center',
  access_confirmation: 'center',
  statement: 'center',
}

const ALIGN_TH_CLASS: Record<'start' | 'center' | 'end', string> = {
  start: '!text-left',
  center: '!text-center',
  end: '!text-right',
}

const ALIGN_TD_CLASS: Record<'start' | 'center' | 'end', string> = {
  start: '!text-left',
  center: '!text-center',
  end: '!text-right',
}

const DEFAULT_COLUMN_ID_ORDER = REIMBURSEMENTS_LIST_COLUMN_OPTIONS.map(
  (column) => column.id,
)

const COLUMN_LABELS: Record<ReimbursementListColumnId, string> =
  Object.fromEntries(
    REIMBURSEMENTS_LIST_COLUMN_OPTIONS.map((column) => [column.id, column.label]),
  ) as Record<ReimbursementListColumnId, string>

function SortHeader({
  field,
  children,
  orderBy,
  sortBy,
  onSort,
  align = 'start',
}: SortHeaderProps) {
  const isActive = orderBy === field
  return (
    <th
      data-testid={`reimbursements-table-sort-${field}`}
      className={cn(
        'cursor-pointer select-none',
        align === 'center' && '!text-center',
        align === 'end' && '!text-right',
        align === 'start' && '!text-left',
      )}
      onClick={() => onSort(field)}
    >
      <button
        type="button"
        className={cn(
          styles.sortHeaderButton,
          align === 'center' && 'w-full justify-center',
          align === 'end' && 'w-full justify-end',
          align === 'start' && 'w-full justify-start',
        )}
        onClick={(event) => {
          event.stopPropagation()
          onSort(field)
        }}
      >
        <span
          className={cn(
            styles.sortHeaderLabel,
            align === 'start' && '!text-left',
            align === 'center' && '!text-center',
            align === 'end' && '!text-right',
          )}
        >
          {children}
        </span>
        <span
          aria-hidden="true"
          className={cn(styles.sortIcon, isActive && styles.sortIconActive)}
        >
          {isActive ? (sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3" />}
        </span>
      </button>
    </th>
  )
}

export function ReimbursementsTable({
  items,
  orderBy,
  sortBy,
  onSort,
  onAccessConfirmation,
  onStatementDownload,
  processingMap,
  visibleColumnIds,
  orderedColumnIds,
  statusLabelMaps,
}: ReimbursementsTableProps) {
  const { roleName, orgId, hasPolicy } = useUserRole()
  const isLibertyRole = roleName
    ? LIBERTY_ROLES.includes(roleName as (typeof LIBERTY_ROLES)[number])
    : false
  const isLibertyOrg = Number(orgId) === 1
  const showProperty = hasPolicy(['default_list_property_view'])
  const showCompany = hasPolicy(['default_list_company_view'])
  const canViewDetail = hasPolicy(['default_detail_view'])
  const canViewCharityDetail = hasPolicy(['charity_default_detail_view'])

  const visibleColumnSet = useMemo(
    () => new Set<ReimbursementListColumnId>(visibleColumnIds ?? DEFAULT_COLUMN_ID_ORDER),
    [visibleColumnIds],
  )
  const isColumnVisible = (columnId: ReimbursementListColumnId) =>
    isReimbursementListLockedColumn(columnId) || visibleColumnSet.has(columnId)

  /** Role/policy gate — whether the column may appear for this user at all. */
  const canRenderColumn = (columnId: ReimbursementListColumnId) => {
    switch (columnId) {
      case 'applicant_id':
        return isLibertyOrg
      case 'property_name':
        return showProperty
      case 'company_name':
        return showCompany
      case 'review_decision':
        return isLibertyRole
      case 'contract_start_date':
        return isLibertyOrg
      case 'adjust_max_movein_date':
        return isLibertyOrg
      case 'access_confirmation':
        return isLibertyOrg
      case 'statement':
        return isLibertyOrg
      default:
        return true
    }
  }

  const tableColumnIds = (orderedColumnIds ?? DEFAULT_COLUMN_ID_ORDER).filter(
    (columnId) => canRenderColumn(columnId) && isColumnVisible(columnId),
  )
  const hasVisibleColumns = tableColumnIds.length > 0

  const tableMinWidth = `${tableColumnIds.reduce((sum, columnId) => sum + COLUMN_WIDTHS[columnId], 0)}rem`

  const sh = (
    field: string,
    children: ReactNode,
    align: 'start' | 'center' | 'end' = 'start',
  ) => (
    <SortHeader
      field={field}
      orderBy={orderBy}
      sortBy={sortBy}
      onSort={onSort}
      align={align}
    >
      {children}
    </SortHeader>
  )

  const plainHeader = (columnId: ReimbursementListColumnId, children: ReactNode) => (
    <th className={ALIGN_TH_CLASS[COLUMN_ALIGN[columnId]]}>{children}</th>
  )

  const cellClass = (columnId: ReimbursementListColumnId, extra?: string) =>
    cn(ALIGN_TD_CLASS[COLUMN_ALIGN[columnId]], extra)

  const renderColumnHeader = (columnId: ReimbursementListColumnId) => {
    const label = COLUMN_LABELS[columnId]
    const align = COLUMN_ALIGN[columnId]
    switch (columnId) {
      case 'applicant_id':
      case 'resident_name':
      case 'property_name':
      case 'company_name':
      case 'agent_name':
      case 'default_paid':
      case 'reimbursement_type':
      case 'reimbursement_rental_status':
      case 'reimbursement_rental_progress':
      case 'review_decision':
      case 'contract_start_date':
      case 'rental_status_date':
      case 'default_status_date':
        return sh(columnId, label, align)
      case 'certificate_amount':
      case 'document_status':
      case 'vintage':
      case 'adjust_max_movein_date':
      case 'access_confirmation':
        return plainHeader(columnId, label)
      case 'statement':
        return <StatementListColumnHeader />
      default:
        return null
    }
  }

  const renderColumnCell = (
    columnId: ReimbursementListColumnId,
    item: ReimbursementListItem,
  ) => {
    switch (columnId) {
      case 'applicant_id':
        return (
          <td className={cellClass(columnId)}>
            {canViewDetail ? (
              <Link
                to={`${ROUTE_BUILDERS.DEFAULT_DETAIL(
                  item.applicant_id,
                  item.fk_user_id,
                  item.reimbursement_id,
                )}#default`}
                className="font-medium text-[rgb(21_151_255)] hover:text-[#0d7acc] hover:underline"
              >
                {padApplicantId(item.applicant_id)}
              </Link>
            ) : (
              padApplicantId(item.applicant_id)
            )}
          </td>
        )
      case 'resident_name':
        return (
          <td className={cellClass(columnId, 'text-slate-700')}>
            {!isLibertyOrg && canViewCharityDetail && item.resident_id ? (
              <a
                href={`/charity-defaults/${item.resident_id}`}
                className="inline-block max-w-full truncate text-[rgb(21_151_255)] hover:underline"
              >
                {item.resident_name}
              </a>
            ) : (
              item.resident_name
            )}
          </td>
        )
      case 'property_name':
        return (
          <td className={cellClass(columnId, 'text-slate-600')}>
            {item.property_name ?? '—'}
          </td>
        )
      case 'company_name':
        return (
          <td className={cellClass(columnId, 'text-slate-600')}>
            {item.company_name ?? '—'}
          </td>
        )
      case 'agent_name':
        return (
          <td className={cellClass(columnId, 'text-slate-600')}>
            {item.agent_name ?? '—'}
          </td>
        )
      case 'certificate_amount':
        return (
          <td className={cellClass(columnId, 'text-slate-600 tabular-nums whitespace-nowrap')}>
            {item.certificate_amount ? formatCurrency(item.certificate_amount) : ''}
          </td>
        )
      case 'default_paid':
        return (
          <td className={cellClass(columnId, 'text-slate-600 tabular-nums whitespace-nowrap')}>
            {item.default_paid ? formatCurrency(item.default_paid) : ''}
          </td>
        )
      case 'reimbursement_type':
        return (
          <td className={cellClass(columnId, 'text-slate-600 whitespace-nowrap')}>
            <span className={reimbursementTypeTextClass(item.reimbursement_type)}>
              {reimbursementTypeLabel(item.reimbursement_type, statusLabelMaps) || '—'}
            </span>
          </td>
        )
      case 'reimbursement_rental_status':
        return (
          <td className={cellClass(columnId, 'text-slate-600 whitespace-nowrap')}>
            <span className={getReimbursementWorkflowStatusPillClassName(item.reimbursement_rental_status)}>
              {item.reimbursement_rental_status ?? '—'}
            </span>
          </td>
        )
      case 'reimbursement_rental_progress': {
        const progressRaw = item.reimbursement_rental_progress
        const progressLabel =
          resolveReimbursementListProgressLabel(
            progressRaw,
            statusLabelMaps?.reimbursement_rental_progress,
          ) ?? progressRaw
        return (
          <td className={cellClass(columnId, 'whitespace-nowrap')}>
            {progressRaw ? (
              <span className={getReimbursementListProgressPillClassName(progressRaw)}>
                {progressLabel}
              </span>
            ) : (
              <span className="text-slate-600">—</span>
            )}
          </td>
        )
      }
      case 'review_decision':
        return (
          <td className={cellClass(columnId, 'text-slate-600')}>
            {item.review_decision ?? '—'}
          </td>
        )
      case 'document_status':
        return (
          <td className={cellClass(columnId, 'text-slate-600')}>
            <div className="inline-flex max-w-full items-center gap-1 align-middle">
              <span className="min-w-0 truncate">{displayDocumentStatus(item.document_status ?? '') || '—'}</span>
              {/* <DocumentStatusInfoIcon item={item} /> */}
            </div>
          </td>
        )
      case 'contract_start_date':
        return (
          <td className={cellClass(columnId, 'text-slate-600 whitespace-nowrap')}>
            {formatDate(item.contract_start_date) || '—'}
          </td>
        )
      case 'vintage':
        return (
          <td className={cellClass(columnId, 'text-slate-600 whitespace-nowrap')}>
            {formatYear(item.contract_start_date) || '—'}
          </td>
        )
      case 'rental_status_date':
        return (
          <td className={cellClass(columnId, 'text-slate-600 whitespace-nowrap')}>
            {formatDate(item.rental_status_date) || '—'}
          </td>
        )
      case 'adjust_max_movein_date':
        return (
          <td className={cellClass(columnId, 'text-slate-600 whitespace-nowrap')}>
            {formatDate(item.adjust_max_movein_date) || '—'}
          </td>
        )
      case 'default_status_date':
        return (
          <td className={cellClass(columnId, 'text-slate-600 whitespace-nowrap')}>
            {formatDate(item.default_status_date) || '—'}
          </td>
        )
      case 'access_confirmation':
        return (
          <td className={cellClass(columnId, 'align-middle whitespace-nowrap')}>
            <AccessConfirmationIconButton
              onClick={() => {
                onAccessConfirmation(item)
              }}
              busy={!!processingMap[`policyDecl-${item.application_id ?? item.applicant_id}`]}
              title="Access Confirmations"
              dataTestId={`reimbursements-access-confirmation-${item.applicant_id}`}
              className="mx-auto"
            />
          </td>
        )
      case 'statement': {
        const progress = String(item.reimbursement_rental_progress ?? '').trim()
        // Angular default-list: `rental_progress === 'Completed'` (label); API may also send COMPLETED.
        const showStatement =
          !!item.default_statement &&
          ['COMPLETED', 'PAID'].includes(progress.toUpperCase())
        return (
          <td className={cellClass(columnId, 'whitespace-nowrap')}>
            {showStatement ? (
              <StatementListDownloadIconButton
                onClick={() => {
                  onStatementDownload(item)
                }}
                aria-label={
                  item.default_statement
                    ? `Open statement: ${item.default_statement}`
                    : 'Open statement'
                }
                dataTestId={`reimbursements-statement-download-${item.applicant_id}`}
              />
            ) : null}
          </td>
        )
      }      default:
        return null
    }
  }

  return (
    <div data-testid="reimbursements-list-table-card" className={styles.tableCard}>
      <div
        data-testid="reimbursements-list-table-scroll"
        className={cn(styles.tableResponsiveScroll, 'touch-scroll')}
      >
        {hasVisibleColumns ? (
          <table
            data-testid="reimbursements-table"
            className={cn(
              styles.listingTable,
              styles.reimbursementsListingTable,
              'w-full !max-w-none [&_thead_th]:!whitespace-nowrap [&_tbody_td:not([colspan])]:!whitespace-normal',
              '[&_thead_th]:break-normal [&_tbody_td:not([colspan])]:break-words',
              '[&_thead_th]:!px-2.5 [&_thead_th]:!py-2 [&_thead_th]:!align-middle',
              '[&_tbody_td]:!px-2.5 [&_tbody_td]:!py-2 [&_tbody_td]:!text-[13px] [&_tbody_td]:!leading-snug',
            )}
            style={{ minWidth: tableMinWidth }}
          >
            <colgroup>
              {tableColumnIds.map((columnId) => (
                <col key={columnId} style={{ width: `${COLUMN_WIDTHS[columnId]}rem` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {tableColumnIds.map((columnId) => (
                  <Fragment key={columnId}>{renderColumnHeader(columnId)}</Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr
                  key={item.reimbursement_id ?? `${item.applicant_id}-${index}`}
                  data-testid={`reimbursements-table-row-${item.reimbursement_id ?? item.applicant_id}`}
                  className="transition-colors"
                >
                  {tableColumnIds.map((columnId) => (
                    <Fragment key={columnId}>{renderColumnCell(columnId, item)}</Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div
            className="flex min-h-[160px] items-center justify-center px-6 py-10 text-center text-sm font-medium text-slate-500"
            data-testid="reimbursements-table-no-columns"
          >
            No columns selected. Use Columns to choose fields for this table.
          </div>
        )}
      </div>
    </div>
  )
}
